import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FaWhatsapp } from "react-icons/fa";
import { MdSms } from "react-icons/md";
import Button from "../../../common/buttons/Button";
import PaymentSuccess from "../payments/PaymentSuccess";
import {
  runCheckoutWidget,
  WIDGET_ERRORS,
} from "../payments/zohoCheckout";
import {
  checkoutCredits,
  getCreditOrder,
  getCreditPacks,
  verifyCreditOrder,
} from "../../../../api/settings/Credits/credits";

const POLL_MS = 4000;

const CHANNEL_STYLES = {
  whatsapp: {
    border: "border-pink",
    icon: "bg-pink text-white",
    accent: "text-pink",
    tint: "bg-pink bg-opacity-10",
    label: "WhatsApp",
    unitLabel: "WhatsApp messages",
  },
  sms: {
    border: "border-primary",
    icon: "bg-primary text-white",
    accent: "text-primary",
    tint: "bg-primary bg-opacity-10",
    label: "SMS",
    unitLabel: "SMS",
  },
};

const money = (n) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const count = (n) => (Number(n) || 0).toLocaleString("en-IN");

/**
 * Buy WhatsApp / SMS credits.
 *
 *   choose credits -> Pay -> Zoho's checkout opens on top of this page
 *   -> the server confirms the payment with Zoho -> "Payment successful".
 *
 * No new tab or window is opened (works the same inside the mobile app).
 * Nothing is invoiced until the money is received. Credits are added by the
 * server once Zoho reports the payment, so they arrive even if this screen
 * is closed right after paying.
 */
const AddCredit = ({ onCredited, onClose, balances }) => {
  const variant = useSelector((state) => state?.auth?.variant) || "";
  const isPro = String(variant).startsWith("Pro");

  const [packs, setPacks] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [qty, setQty] = useState({ whatsapp: 0, sms: 0 });
  // select | starting | paying (widget open) | confirming | success | failed
  const [phase, setPhase] = useState("select");
  const [order, setOrder] = useState(null);
  const [message, setMessage] = useState("");
  const [checking, setChecking] = useState(false);

  // Essentials clinics have no WhatsApp, so only Pro sees that pack.
  const visiblePacks = packs.filter((p) => p.channel !== "whatsapp" || isPro);

  useEffect(() => {
    getCreditPacks()
      .then((response) => setPacks(response?.data?.data || []))
      .catch(() =>
        setLoadError("Credit packs could not be loaded. Please try again.")
      );
  }, []);

  /* ------------------------- totals ------------------------- */
  const lines = visiblePacks
    .filter((p) => qty[p.channel] > 0)
    .map((p) => {
      const credits = qty[p.channel];
      const base = p.rate * credits;
      const gst = (base * p.taxPercentage) / 100;
      return {
        ...p,
        credits,
        messages: credits * p.unit,
        base,
        gst,
        incl: base + gst,
      };
    });
  const subTotal = lines.reduce((s, l) => s + l.base, 0);
  const gstTotal = lines.reduce((s, l) => s + l.gst, 0);
  // Zoho rounds each invoice to the nearest rupee, so show what is charged.
  const exactTotal = subTotal + gstTotal;
  const total = Math.round(exactTotal);
  const roundOff = total - exactTotal;
  const taxPct = lines[0]?.taxPercentage ?? 18;

  const changeQty = (channel, delta) =>
    setQty((q) => ({ ...q, [channel]: Math.max(0, (q[channel] || 0) + delta) }));

  /* ---------------------- order checking --------------------- */
  const checkOrder = useCallback(
    async (orderId) => {
      try {
        const response = await getCreditOrder(orderId);
        const latest = response?.data?.data;
        if (!latest) return "pending";

        if (latest.status === "credited") {
          setOrder(latest);
          setPhase("success");
          if (onCredited) onCredited(latest);
          return "done";
        }
        if (["failed", "expired"].includes(latest.status)) {
          setOrder(latest);
          setMessage(latest.statusNote || "This payment did not complete.");
          setPhase("failed");
          return "done";
        }
      } catch (e) {
        /* a failed check is not a failed payment — keep waiting */
      }
      return "pending";
    },
    [onCredited]
  );

  useEffect(() => {
    if (phase !== "confirming" || !order?._id) return undefined;

    let stopped = false;
    let timer = null;

    const loop = async () => {
      if (stopped) return;
      const result = await checkOrder(order._id);
      if (!stopped && result === "pending") {
        timer = setTimeout(loop, POLL_MS);
      }
    };
    loop();

    // Coming back to the app (e.g. after a UPI app): check at once.
    const checkNow = () => {
      if (stopped || document.visibilityState === "hidden") return;
      clearTimeout(timer);
      loop();
    };
    document.addEventListener("visibilitychange", checkNow);
    window.addEventListener("focus", checkNow);

    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", checkNow);
      window.removeEventListener("focus", checkNow);
    };
  }, [phase, order?._id, checkOrder]);

  /* --------------------------- pay --------------------------- */
  const applyResult = (latest) => {
    if (latest?.status === "credited") {
      setOrder(latest);
      setPhase("success");
      if (onCredited) onCredited(latest);
      return true;
    }
    if (latest && ["failed", "expired"].includes(latest.status)) {
      setOrder(latest);
      setMessage(latest.statusNote || "This payment did not complete.");
      setPhase("failed");
      return true;
    }
    return false;
  };

  const handlePay = async () => {
    if (lines.length === 0) return;
    setMessage("");
    setPhase("starting");

    let created;
    try {
      const response = await checkoutCredits(
        lines.map((l) => ({ channel: l.channel, quantity: l.credits }))
      );
      created = response?.data?.data;
      setOrder(created);
    } catch (error) {
      setMessage(
        error?.response?.data?.error ||
          "Online payment is not available right now. Please contact support."
      );
      setPhase("select");
      return;
    }

    setPhase("paying");
    let outcome = null;
    try {
      outcome = await runCheckoutWidget(created.widget);
    } catch (error) {
      const closed = error?.code === "widget_closed";
      // Closing the window may still have followed a completed payment, so
      // ask the server before treating it as a cancellation.
      try {
        const response = await verifyCreditOrder(created._id);
        if (applyResult(response?.data?.data)) return;
      } catch (e) {
        /* fall through to the message below */
      }
      setMessage(
        closed
          ? "Payment was cancelled. No money was taken."
          : WIDGET_ERRORS[error?.code] ||
              error?.message ||
              "The payment window could not be opened. Please try again."
      );
      setOrder(null);
      setPhase("select");
      return;
    }

    setPhase("confirming");
    try {
      const response = await verifyCreditOrder(created._id, outcome);
      const latest = response?.data?.data;
      if (!applyResult(latest) && latest?.paymentMismatch) {
        setMessage(
          "We could not confirm a payment for this order. If money was " +
            "debited it will be credited automatically; otherwise please try again."
        );
        setOrder(null);
        setPhase("select");
      }
    } catch (e) {
      /* the waiting screen keeps checking */
    }
  };

  const manualCheck = async () => {
    if (!order?._id) return;
    setChecking(true);
    await checkOrder(order._id);
    setChecking(false);
  };

  const reset = () => {
    setOrder(null);
    setQty({ whatsapp: 0, sms: 0 });
    setMessage("");
    setPhase("select");
  };

  /* ------------------------- rendering ------------------------ */
  if (phase === "success" && order) {
    return (
      <PaymentSuccess
        order={order}
        onDone={onClose}
        onMore={reset}
        moreLabel="Buy more"
      />
    );
  }

  if (phase === "paying" || phase === "starting" || phase === "confirming") {
    const confirming = phase === "confirming";
    return (
      <div className="w-full max-w-[560px] mx-auto">
        <div className="flex items-center justify-between rounded-15 bg-secondary px-4 py-3">
          <div>
            <p className="text-bodyBB text-darkgrey">
              {confirming ? "Confirming your payment" : "Complete your payment"}
            </p>
            <p className="text-smallRB text-darkgrey opacity-80">
              Secure payment by Zoho Payments
            </p>
          </div>
          {order && (
            <p className="text-heading2B text-darkgrey">{money(order.total)}</p>
          )}
        </div>

        <div className="flex flex-col items-center py-14 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="mt-3 text-bodyRB text-darkgrey">
            {confirming
              ? "Payment received — confirming with the bank…"
              : phase === "starting"
              ? "Preparing your payment…"
              : "Finish the payment in the secure window."}
          </p>
          {confirming && (
            <>
              <p className="mt-1 text-smallRB text-darkgrey opacity-70 max-w-[360px]">
                This screen updates by itself. You can also close it — your
                credits are added automatically once the payment is confirmed.
              </p>
              <div className="mt-4 w-[170px]">
                <Button
                  type="secondary"
                  action="button"
                  className="!py-[9px] text-bodySBB"
                  onClick={manualCheck}
                  loading={checking}
                >
                  Check again
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (phase === "failed") {
    return (
      <div className="w-full max-w-[460px] mx-auto text-center py-8">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-danger bg-opacity-20 text-[34px] text-danger">
          !
        </div>
        <h2 className="text-heading2B text-darkgrey">Payment not completed</h2>
        <p className="text-bodyRB text-darkgrey mt-2">{message}</p>
        <div className="mt-6 flex justify-center">
          <div className="w-[180px]">
            <Button type="primary" action="button" onClick={reset}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[560px] mx-auto">
      <div className="text-center">
        <p className="text-bodyRB text-darkgrey opacity-80">
          Top up your messaging credits. Unused credits expire with your
          subscription.
        </p>
      </div>

      {balances && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          {visiblePacks.map((p) => (
            <div
              key={p.channel}
              className="rounded-15 border border-[#E6EAF0] bg-[#F7F9FB] px-4 py-2"
            >
              <p className="text-smallRB text-darkgrey opacity-70">
                {CHANNEL_STYLES[p.channel].label} balance
              </p>
              <p className="text-bodyBB text-darkgrey">
                {count(balances[p.channel])} messages
              </p>
            </div>
          ))}
        </div>
      )}

      {loadError && (
        <p className="text-bodyRB text-danger text-center mt-3">{loadError}</p>
      )}
      {message && (
        <p className="text-bodyRB text-danger text-center mt-3">{message}</p>
      )}

      <div className="mt-4 flex flex-col gap-3">
        {visiblePacks.map((pack) => (
          <PackCard
            key={pack.channel}
            pack={pack}
            line={lines.find((l) => l.channel === pack.channel)}
            credits={qty[pack.channel] || 0}
            onChange={(delta) => changeQty(pack.channel, delta)}
          />
        ))}
        {packs.length === 0 && !loadError && (
          <p className="text-bodyRB text-darkgrey text-center py-6">
            Loading credit packs…
          </p>
        )}
      </div>

      {lines.length > 0 && (
        <div className="mt-4 rounded-15 border border-[#E6EAF0] bg-white p-4 text-bodyRB text-darkgrey">
          <p className="text-bodyBB mb-2">Order summary</p>
          {lines.map((l) => (
            <div key={l.channel} className="flex justify-between py-0.5">
              <span>
                {CHANNEL_STYLES[l.channel].label} · {l.credits} credit
                {l.credits > 1 ? "s" : ""} ({count(l.messages)} messages)
              </span>
              <span>{money(l.base)}</span>
            </div>
          ))}
          <div className="mt-2 border-t pt-2">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>{money(subTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>GST ({taxPct}%)</span>
              <span>{money(gstTotal)}</span>
            </div>
            {Math.abs(roundOff) >= 0.005 && (
              <div className="flex justify-between">
                <span>Round off</span>
                <span>
                  {roundOff < 0 ? "−" : "+"}
                  {money(Math.abs(roundOff))}
                </span>
              </div>
            )}
          </div>
          <div className="mt-2 border-t pt-2 flex justify-between text-bodyBB">
            <span>Total payable</span>
            <span>{money(total)}</span>
          </div>
        </div>
      )}

      <div className="mt-5 w-full flex justify-center">
        <div className="w-full max-w-[320px]">
          <Button
            type="primary"
            action="button"
            className="text-heading2B !p-2 !h-[50px]"
            onClick={handlePay}
            disabled={lines.length === 0}
          >
            {lines.length === 0 ? "Choose credits" : `Pay ${money(total)}`}
          </Button>
        </div>
      </div>
      <p className="mt-3 text-center text-smallRB text-darkgrey opacity-60">
        Secure payment by Zoho Payments · UPI &amp; Net Banking
      </p>
    </div>
  );
};

const PackCard = ({ pack, line, credits, onChange }) => {
  const style = CHANNEL_STYLES[pack.channel];
  const Icon = pack.channel === "whatsapp" ? FaWhatsapp : MdSms;

  return (
    <div
      className={`rounded-15 border ${
        credits > 0 ? style.border : "border-[#E6EAF0]"
      } bg-white overflow-hidden`}
    >
      <div className="flex items-center gap-3 p-4">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[22px] ${style.icon}`}
        >
          <Icon />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-bodyBB text-darkgrey">{pack.name}</p>
          <p className={`text-smallRB ${style.accent}`}>
            1 credit = {count(pack.unit)} {style.unitLabel}
          </p>
          <p className="text-smallRB text-darkgrey opacity-80">
            {money(pack.rate)} + {pack.taxPercentage}% GST per credit
          </p>
        </div>

        <div className="flex h-[42px] w-[118px] shrink-0 items-center justify-between rounded-15 border border-[#D5DBE3] bg-white px-1">
          <button
            type="button"
            aria-label={`Fewer ${pack.name}`}
            onClick={() => onChange(-1)}
            disabled={credits === 0}
            className="h-9 w-9 rounded-[10px] text-[20px] leading-none text-darkgrey disabled:opacity-30 hover:bg-[#F0F3F7]"
          >
            −
          </button>
          <span className="text-bodyBB text-darkgrey min-w-[24px] text-center">
            {credits}
          </span>
          <button
            type="button"
            aria-label={`More ${pack.name}`}
            onClick={() => onChange(1)}
            className="h-9 w-9 rounded-[10px] text-[20px] leading-none text-darkgrey hover:bg-[#F0F3F7]"
          >
            +
          </button>
        </div>
      </div>

      {line && (
        <div
          className={`flex flex-wrap items-center justify-between gap-2 px-4 py-3 ${style.tint}`}
        >
          <div className="text-bodyRB text-darkgrey">
            <span className="text-bodyBB">
              {line.credits} credit{line.credits > 1 ? "s" : ""}
            </span>
            {" · "}
            <span className="text-bodyBB">{count(line.messages)}</span> messages
          </div>
          <div className="text-right">
            <p className="text-bodyBB text-darkgrey">
              {money(line.incl)}{" "}
              <span className="text-smallRB opacity-70">incl. GST</span>
            </p>
            <p className="text-smallRB text-darkgrey opacity-70">
              {money(line.base)} + {money(line.gst)} GST
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCredit;
